import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { categoriesApi } from "@api";
import { ITour } from "@interfaces";
import { uniqBy, keys } from "lodash";
import { filterProducts } from "@utilities";
import { RootState } from ".";

interface ToursCategoryState {
    finalToursByCategory: Record<string, ITour[]>;
    isFetchingComplete: boolean;
    loading: boolean;
    error?: string;
}

const initialState: ToursCategoryState = {
    finalToursByCategory: {},
    isFetchingComplete: false,
    loading: false,
    error: undefined,
};

export interface CategoriesResponse {
    data: {
        data: Record<string, string[]>;
    };
}

export interface ResultsResponse {
    data: {
        results: any[];
    };
}

export const fetchToursByCategory = createAsyncThunk<
    Record<string, ITour[]>,
    void,
    { rejectValue: string }
>("toursCategory/fetchToursByCategory", async (_, thunkAPI) => {
    try {
        const [categoryRes, filtersRes] = await Promise.all([
            categoriesApi.getCategories().then((res) => {
                return (res.data as CategoriesResponse["data"]).data;
            }),
            categoriesApi.getSearchFilters().then((res) => {
                const data = res.data?.data ?? res.data;
                const typeMap = new Map<string, string>();
                ["accommodation", "activity", "transport"].forEach((type) => {
                    const items = (data as any)[type] || [];
                    items.forEach((item: any) => {
                        typeMap.set(item.value, type === "activity" ? "activity" : type);
                    });
                });
                return { typeMap };
            }),
        ]);

        const categoryData: Record<string, string[]> = categoryRes || {};
        if (keys(categoryData).length === 0) {
            return {};
        }

        const processedValues = new Set<string>();
        const apiCallList: Array<{ categoryName: string; filterValue: string; filterType: string }> = [];

        keys(categoryData).forEach((categoryName) => {
            const items = categoryData[categoryName] || [];
            items.forEach((itemName) => {
                if (processedValues.has(itemName)) return;
                const filterType = filtersRes.typeMap.get(itemName);
                if (filterType) {
                    processedValues.add(itemName);
                    apiCallList.push({ categoryName, filterValue: itemName, filterType });
                }
            });
        });

        const finalData: Record<string, ITour[]> = {};
        const queue = [...apiCallList];

        while (queue.length > 0) {
            const batch = queue.splice(0, 4);
            const promises = batch.map(({ categoryName, filterValue, filterType }) => {
                return categoriesApi.searchProducts({
                    [filterType]: filterValue,
                    country: "Australia",
                    per_page: 20,
                }).then((res) => {
                    const responseData = res.data?.data ?? res.data;
                    const products = filterProducts(responseData.products || []);
                    const tours: ITour[] = products.map((tour: any) => ({
                        productId: tour.productId,
                        name: tour.name,
                        location: tour.categories
                            ? `${tour.categories.startLocationName || ""}, ${(tour.categories.countryNames || [])[0] || ""}`
                            : tour.location || "",
                        durationDays: tour.durationDays,
                        durationNight: tour.durationNight,
                        rrp: Number(tour.rrp).toFixed(2),
                        productImagePath: tour.productLargeSizeImagePath || tour.productImagePath,
                        categories: tour.categories || { startLocationName: "", countryNames: [] },
                    })) as unknown as ITour[];
                    return { categoryName, tours };
                });
            });

            // eslint-disable-next-line no-await-in-loop
            const batchResults = await Promise.all(promises);

            batchResults.forEach(({ categoryName, tours }) => {
                if (!finalData[categoryName]) {
                    finalData[categoryName] = [];
                }
                finalData[categoryName] = uniqBy(
                    [...finalData[categoryName], ...tours],
                    "productId",
                );
            });

            thunkAPI.dispatch(updatePartialTours(finalData));
        }

        return finalData;
    } catch (error) {
        console.error(error);
        return thunkAPI.rejectWithValue("Failed to fetch tours by category");
    }
});

const toursCategorySlice = createSlice({
    name: "toursCategory",
    initialState,
    reducers: {
        updatePartialTours: (state, action: PayloadAction<Record<string, ITour[]>>) => {
            keys(action.payload).forEach((category) => {
                if (!state.finalToursByCategory[category]) {
                    state.finalToursByCategory[category] = [];
                }
                // Merge and deduplicate tours.
                state.finalToursByCategory[category] = uniqBy(
                    [...state.finalToursByCategory[category], ...action.payload[category]],
                    "productId",
                );
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchToursByCategory.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(
            fetchToursByCategory.fulfilled,
            (state, action: PayloadAction<Record<string, ITour[]>>) => {
                state.loading = false;
                // Use deduplicated final data.
                state.finalToursByCategory = {};
                keys(action.payload).forEach((category) => {
                    state.finalToursByCategory[category] = uniqBy(
                        action.payload[category],
                        "productId",
                    );
                });
                state.isFetchingComplete = true;
            },
        );
        builder.addCase(fetchToursByCategory.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export const { updatePartialTours } = toursCategorySlice.actions;
export const selectCategoryTours = (state: RootState) => state.toursCategory;
export default toursCategorySlice.reducer;
